import { CursorState } from '@editor/config/constants/cursor'
import { AnchorColors, CircleAnchorProps } from '@editor/interfaces/canvas'
import { SubtoolVertex } from '@editor/interfaces/tool'
import { CircleAnchorIcon } from '@infraloop/common'

import { DetectionKind } from './interfaces'

export const CRACK_DETECTION_POLYGON_ID = 'polyline-crack-detection-polygon'
export const CRACK_DETECTION_PCD_ID = 'polyline-crack-detection-pcd'

/**
 * Fixed thickness for minimum bounding box generation.
 * Value is in world units.
 */
export const MINIMUM_BOUNDARY_THICKNESS = 0.1

/**
 * Thickness of the slab a チョーキング run crops to. Value is in world units.
 *
 * The drawn outline is planar; the surface under it usually is not. On a tunnel wall the mesh
 * bows away from the plane fitted through the drawn vertices, furthest at the middle of the
 * span — so with a slab this thin the middle of a wide box fell outside what the detector can
 * see, and the colour pick refused it.
 *
 * The bow grows with the square of the span (roughly `c²/8r`): about 0.125 m across a 2 m box on
 * a 4 m radius, against 0.03 m for a 1 m one. The slab is centred on the plane, so it needs half
 * its thickness on the deep side, plus room for the surface roughness of bare concrete.
 *
 * Chalk gets the deeper slab because its boxes may be twice as wide as crack detection's (2 m
 * against 1 m); crack detection keeps the tight crop its width measurement relies on.
 */
export const CHALK_BOUNDARY_THICKNESS = 0.3

/**
 * Padding added to the minimum bounding box extent, only to the sides.
 * Value is in world units.
 */
export const MINIMUM_BOUNDARY_PADDING = 0.025

/**
 * The slab thickness a detection run of this kind crops to.
 *
 * The same value gates the colour pick client-side and sizes the bounds sent to the detector, so
 * that a point the pick accepts is one the detector can actually see.
 *
 * @param kind What the run is looking for.
 */
export const boundaryThicknessFor = (kind: DetectionKind): number =>
  kind === DetectionKind.Chalk ? CHALK_BOUNDARY_THICKNESS : MINIMUM_BOUNDARY_THICKNESS

export enum AnchorStates {
  Base = 'base',
  Highlighted = 'highlighted',
}

/**
 * Default styles for anchors.
 */
export const DefaultStyles: Partial<CircleAnchorProps> = {
  color: 'red',
}

/**
 * Default styles for when vertex subtool is active.
 */
export const DefaultAnchorStyles: Partial<CircleAnchorProps> = {
  color: AnchorColors.Movable,
  icon: CircleAnchorIcon.MOVE,
  iconColor: 'white',
  outlineOpacity: 0,
  iconScale: 1.4,
  scale: 1.3,
  hoverScale: 1.5,
}

/**
 * Styles when no action is possible on the anchor.
 */
export const NoActionStyles: Partial<CircleAnchorProps> = {
  ...DefaultAnchorStyles,
  color: AnchorColors.Normal,
  outlineOpacity: 1,
  icon: undefined,
}

/**
 * Styles for each vertex subtool.
 */
export const VertexSubtoolStyles: Partial<
  Record<SubtoolVertex, Partial<Record<AnchorStates, Partial<CircleAnchorProps>>>>
> = {
  [SubtoolVertex.AddVertex]: { [AnchorStates.Base]: DefaultAnchorStyles },
  [SubtoolVertex.MoveVertex]: { [AnchorStates.Base]: DefaultAnchorStyles },
  [SubtoolVertex.RemoveVertex]: {
    [AnchorStates.Base]: {
      ...DefaultAnchorStyles,
      icon: CircleAnchorIcon.REMOVE,
      color: 'red',
    },
  },
  [SubtoolVertex.ConnectVertex]: {
    [AnchorStates.Base]: {
      ...DefaultAnchorStyles,
      color: '#63b3ed', // blue.200
      icon: CircleAnchorIcon.CHAIN,
    },
    [AnchorStates.Highlighted]: {
      iconScale: (DefaultAnchorStyles.iconScale || 1) * 1.2,
      scale: (DefaultAnchorStyles.scale || 1) * 1.2,
      hoverScale: (DefaultAnchorStyles.hoverScale || 1) * 1.2,
      color: '#68D391', // green.300
      icon: CircleAnchorIcon.CHECK,
    },
  },
}

/**
 * Different cursor states for each vertex subtool.
 * This defines what cursor to show when hovering over anchors.
 */
export const Cursors: Partial<Record<SubtoolVertex, CursorState>> = {
  [SubtoolVertex.RemoveVertex]: CursorState.POINTER,
}
